/**
 * Path Animation
 * Animates objects along custom bezier curves and paths
 */

import { Coords, ViewItem } from 'src/types';
import { AnimationEngine } from './AnimationEngine';
import { AnimationConfig } from './animationTypes';
import { lerp, clamp } from './utils';

export interface PathPoint extends Coords {
  /** Optional control point for bezier curves */
  cp1?: Coords;
  cp2?: Coords;
}

export interface PathAnimationConfig extends AnimationConfig {
  /** Whether to rotate the object to follow the path */
  autoRotate?: boolean;
  /** Additional rotation offset in degrees */
  rotationOffset?: number;
  /** Whether to ease the path following (smooth acceleration/deceleration) */
  easePath?: boolean;
}

export class PathAnimation {
  private engine: AnimationEngine;

  constructor(engine: AnimationEngine) {
    this.engine = engine;
  }

  /**
   * Animate an object along a path
   */
  public animateAlongPath(
    target: ViewItem,
    path: PathPoint[],
    config: PathAnimationConfig = {}
  ): string {
    const {
      duration = 2000,
      autoRotate = true,
      rotationOffset = 0,
      easePath = true,
      easing = easePath ? 'easeInOutQuad' : 'linear',
      ...restConfig
    } = config;

    // Store original position
    // const startPos = { ...target.tile };

    // Calculate total path length for proper speed distribution
    const segments = this.calculatePathSegments(path);
    const totalLength = segments.reduce((sum, seg) => sum + seg.length, 0);

    return this.engine.animate(
      target,
      { _pathProgress: 1 },
      {
        duration,
        easing,
        onUpdate: (progress) => {
          // Get position along path
          const position = this.getPointOnPath(path, segments, totalLength, progress);

          // Update target position
          target.tile.x = position.x;
          target.tile.y = position.y;

          // Calculate rotation if needed
          if (autoRotate) {
            const rotation = this.getPathRotation(path, segments, totalLength, progress);
            (target as any)._rotation = rotation + rotationOffset;
          }
        },
        onComplete: () => {
          // Clean up temporary properties
          delete (target as any)._pathProgress;
          delete (target as any)._rotation;
        },
        ...restConfig
      }
    );
  }

  /**
   * Create a circular path animation
   */
  public animateCircular(
    target: ViewItem,
    center: Coords,
    radius: number,
    config: PathAnimationConfig = {}
  ): string {
    const {
      duration = 3000,
      repeat = -1,
      ...restConfig
    } = config;

    return this.engine.animate(
      target,
      { _angle: 360 },
      {
        duration,
        repeat,
        easing: 'linear',
        onUpdate: (progress) => {
          const angle = progress * Math.PI * 2;
          target.tile.x = center.x + Math.cos(angle) * radius;
          target.tile.y = center.y + Math.sin(angle) * radius;

          if (config.autoRotate) {
            (target as any)._rotation = (progress * 360) + (config.rotationOffset || 0);
          }
        },
        ...restConfig
      }
    );
  }

  /**
   * Create a figure-8 path animation
   */
  public animateFigure8(
    target: ViewItem,
    center: Coords,
    width: number,
    height: number,
    config: PathAnimationConfig = {}
  ): string {
    return this.engine.animate(
      target,
      { _progress: 1 },
      {
        duration: config.duration || 4000,
        repeat: config.repeat ?? -1,
        easing: 'linear',
        onUpdate: (progress) => {
          const t = progress * Math.PI * 2;
          const scale = 2 / (3 - Math.cos(2 * t));

          target.tile.x = center.x + (width * scale * Math.cos(t)) / 2;
          target.tile.y = center.y + (height * scale * Math.sin(2 * t)) / 4;
        },
        ...config
      }
    );
  }

  /**
   * Create a spiral path animation
   */
  public animateSpiral(
    target: ViewItem,
    center: Coords,
    startRadius: number,
    endRadius: number,
    revolutions: number = 3,
    config: PathAnimationConfig = {}
  ): string {
    return this.engine.animate(
      target,
      { _progress: 1 },
      {
        duration: config.duration || 3000,
        easing: config.easing || 'easeInOutQuad',
        onUpdate: (progress) => {
          const angle = progress * Math.PI * 2 * revolutions;
          const radius = lerp(startRadius, endRadius, progress);

          target.tile.x = center.x + Math.cos(angle) * radius;
          target.tile.y = center.y + Math.sin(angle) * radius;

          if (config.autoRotate) {
            (target as any)._rotation = (angle * 180 / Math.PI) + (config.rotationOffset || 0);
          }
        },
        ...config
      }
    );
  }

  /**
   * Calculate path segments and their lengths
   */
  private calculatePathSegments(path: PathPoint[]): Array<{ from: PathPoint; to: PathPoint; length: number }> {
    const segments: Array<{ from: PathPoint; to: PathPoint; length: number }> = [];

    for (let i = 0; i < path.length - 1; i++) {
      const from = path[i];
      const to = path[i + 1];

      // Simple linear distance for now
      // For bezier curves, this would need to be more sophisticated
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      segments.push({ from, to, length });
    }

    return segments;
  }

  /**
   * Get a point on the path at a given progress (0-1)
   */
  private getPointOnPath(
    path: PathPoint[],
    segments: Array<{ from: PathPoint; to: PathPoint; length: number }>,
    totalLength: number,
    progress: number
  ): Coords {
    if (path.length === 0) return { x: 0, y: 0 };
    if (path.length === 1) return { x: path[0].x, y: path[0].y };

    const targetDistance = totalLength * clamp(progress, 0, 1);
    let currentDistance = 0;

    for (const segment of segments) {
      const segmentEnd = currentDistance + segment.length;

      if (targetDistance <= segmentEnd) {
        // We're in this segment
        const segmentProgress = (targetDistance - currentDistance) / segment.length;

        // Check if this is a bezier segment
        if (segment.from.cp2 || segment.to.cp1) {
          return this.getPointOnBezier(
            segment.from,
            segment.from.cp2 || segment.from,
            segment.to.cp1 || segment.to,
            segment.to,
            segmentProgress
          );
        } else {
          // Linear interpolation
          return {
            x: lerp(segment.from.x, segment.to.x, segmentProgress),
            y: lerp(segment.from.y, segment.to.y, segmentProgress)
          };
        }
      }

      currentDistance = segmentEnd;
    }

    // Return last point if we've gone past the end
    return { x: path[path.length - 1].x, y: path[path.length - 1].y };
  }

  /**
   * Get rotation angle at a point on the path
   */
  private getPathRotation(
    path: PathPoint[],
    segments: Array<{ from: PathPoint; to: PathPoint; length: number }>,
    totalLength: number,
    progress: number
  ): number {
    const epsilon = 0.001;
    const p1 = this.getPointOnPath(path, segments, totalLength, Math.max(0, progress - epsilon));
    const p2 = this.getPointOnPath(path, segments, totalLength, Math.min(1, progress + epsilon));

    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
    return angle * 180 / Math.PI;
  }

  /**
   * Calculate a point on a cubic bezier curve
   */
  private getPointOnBezier(p0: Coords, p1: Coords, p2: Coords, p3: Coords, t: number): Coords {
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;

    return {
      x: mt3 * p0.x + 3 * mt2 * t * p1.x + 3 * mt * t2 * p2.x + t3 * p3.x,
      y: mt3 * p0.y + 3 * mt2 * t * p1.y + 3 * mt * t2 * p2.y + t3 * p3.y
    };
  }
}

/**
 * Create bezier curve path points
 */
export function createBezierPath(
  start: Coords,
  end: Coords,
  control1?: Coords,
  control2?: Coords
): PathPoint[] {
  const defaultControl1 = control1 || {
    x: start.x + (end.x - start.x) * 0.25,
    y: start.y
  };

  const defaultControl2 = control2 || {
    x: start.x + (end.x - start.x) * 0.75,
    y: end.y
  };

  return [
    { ...start, cp2: defaultControl1 },
    { ...end, cp1: defaultControl2 }
  ];
}

/**
 * Create arc path points
 */
export function createArcPath(
  center: Coords,
  radius: number,
  startAngle: number,
  endAngle: number,
  steps: number = 20
): PathPoint[] {
  const points: PathPoint[] = [];
  const angleRange = endAngle - startAngle;

  for (let i = 0; i <= steps; i++) {
    const progress = i / steps;
    const angle = startAngle + angleRange * progress;
    const radians = angle * Math.PI / 180;

    points.push({
      x: center.x + Math.cos(radians) * radius,
      y: center.y + Math.sin(radians) * radius
    });
  }

  return points;
}